import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from '../ui/badge';
import { CreditCard, Zap, Shield, AlertCircle } from 'lucide-react';

interface BillingInfo {
  access: {
    hasAccess: boolean;
    accessType: 'access_code' | 'trial' | 'pay_per_use';
    remainingTrialPages?: number;
  };
  pricing: {
    perPageCents: number;
    trialPagesLimit: number;
  };
  billing: {
    pages_processed_current_month: number;
    access_code_used?: string;
  };
}

interface BillingStatusProps {
  className?: string;
}

export const BillingStatus: React.FC<BillingStatusProps> = ({ className = '' }) => {
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBillingStatus();
  }, []);

  const fetchBillingStatus = async () => {
    try {
      const { getApiUrl } = await import('../lib/config');
      const token = localStorage.getItem('orca_auth_token');
      
      if (!token) {
        setError('Not authenticated');
        setIsLoading(false);
        return;
      }

      const response = await fetch(getApiUrl('/api/billing/status'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBillingInfo(data);
      } else {
        setError('Failed to fetch billing status');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-card/50 border border-border/50 rounded-lg p-4 transition-all duration-200 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-muted/50 rounded w-1/2 mb-2"></div>
          <div className="h-3 bg-muted/50 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (error || !billingInfo) {
    return (
      <div className={`bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 transition-all duration-200 ${className}`}>
        <div className="flex items-center gap-2 text-yellow-400">
          <AlertCircle size={16} />
          <span className="text-sm">Unable to load billing information</span>
        </div>
      </div>
    );
  }

  const { access, pricing, billing } = billingInfo;
  const remainingTrialPages = access.remainingTrialPages || 0;
  const pagesUsed = billing.pages_processed_current_month || 0;
  const perPageDollars = pricing.perPageCents / 100;

  // Access code users (unlimited)
  if (access.accessType === 'access_code') {
    return (
      <div className={`bg-card/60 border border-green-500/30 rounded-lg p-4 transition-all duration-300 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="text-green-400" size={16} />
            <div>
              <h3 className="text-sm font-medium text-foreground">Unlimited Access</h3>
              <p className="text-sm text-muted-foreground">
                Access code active • No charges apply
              </p>
            </div>
          </div>
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs px-2 py-0.5">
            FREE
          </Badge>
        </div>
        {pagesUsed > 0 && (
          <div className="mt-3 pt-3 border-t border-border/50">
            <p className="text-sm text-muted-foreground">
              Pages processed this month: <strong className="text-foreground">{pagesUsed}</strong>
            </p>
          </div>
        )}
      </div>
    );
  }

  // Trial users
  if (access.accessType === 'trial' && remainingTrialPages > 0) {
    const trialProgress = ((pricing.trialPagesLimit - remainingTrialPages) / pricing.trialPagesLimit) * 100;
    
    return (
      <div className={`bg-card/60 border border-blue-500/30 rounded-lg p-4 transition-all duration-300 ${className}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Zap className="text-blue-400" size={16} />
            <div>
              <h3 className="text-sm font-medium text-foreground">Trial Account</h3>
              <p className="text-sm text-muted-foreground">
                {remainingTrialPages} free pages remaining
              </p>
            </div>
          </div>
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs px-2 py-0.5">
            TRIAL
          </Badge>
        </div>
        
        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex justify-between text-sm text-muted-foreground mb-1">
            <span>Trial usage</span>
            <span className="text-foreground">{pricing.trialPagesLimit - remainingTrialPages}/{pricing.trialPagesLimit} pages</span>
          </div>
          <div className="w-full bg-muted/30 rounded-full h-2">
            <div 
              className="bg-blue-400 h-2 rounded-full transition-all duration-300"
              style={{ width: `${trialProgress}%` }}
            ></div>
          </div>
        </div>

        <div className="text-sm text-muted-foreground bg-card/80 border border-border/50 rounded p-2">
          After trial: <strong className="text-foreground">${perPageDollars.toFixed(2)} per page</strong>
        </div>
      </div>
    );
  }

  // Pay-per-use users
  return (
    <div className={`bg-card/60 border border-orange-500/30 rounded-lg p-4 transition-all duration-300 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CreditCard className="text-orange-400" size={16} />
          <div>
            <h3 className="text-sm font-medium text-foreground">Pay Per Page</h3>
            <p className="text-sm text-muted-foreground">
              ${perPageDollars.toFixed(2)} per page processed
            </p>
          </div>
        </div>
        <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs px-2 py-0.5">
          PAY-PER-USE
        </Badge>
      </div>

      <div className="space-y-2 text-sm text-muted-foreground">
        <div className="flex justify-between">
          <span>Pages processed this month:</span>
          <strong className="text-foreground">{pagesUsed}</strong>
        </div>
        <div className="flex justify-between">
          <span>Trial pages used:</span>
          <strong className="text-foreground">{pricing.trialPagesLimit}</strong>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-border/50">
        <div className="text-sm text-muted-foreground text-center">
          <CreditCard size={16} className="mx-auto mb-2" />
          <p>Payment will be processed automatically when you start a workflow that requires billing.</p>
          <p className="text-xs mt-2">No setup required - pay as you go!</p>
        </div>
      </div>
    </div>
  );
};

export default BillingStatus;
